/**
 * Car image upload pipeline (browser-side).
 *
 * For every uploaded source image we generate 9 derivatives:
 *   - 3 widths: 960, 1280, 1920 (capped to source width)
 *   - 3 formats: AVIF (WASM via @jsquash/avif), WebP (Canvas), JPG (Canvas)
 *
 * Each derivative is uploaded to the `car-images` storage bucket under
 *   `{userId}/{imageId}/{width}.{ext}`
 * and registered in the `car_image_manifest` table so feeds (Google
 * Shopping, Meta) and the picture-tag renderer can pick the right format
 * per consumer.
 *
 * The "primary" 1280-WebP URL is returned as the canonical URL we keep
 * appending to `cars.images` so existing read paths keep working.
 */

import { supabase } from "@/integrations/supabase/client";
import { validateImageFile } from "@/lib/image-utils";

export const DERIVATIVE_WIDTHS = [960, 1280, 1920] as const;
export type DerivativeWidth = (typeof DERIVATIVE_WIDTHS)[number];

export type DerivativeFormat = "avif" | "webp" | "jpg";

export const DERIVATIVE_FORMATS: DerivativeFormat[] = ["avif", "webp", "jpg"];

const MIME_BY_FORMAT: Record<DerivativeFormat, string> = {
  avif: "image/avif",
  webp: "image/webp",
  jpg: "image/jpeg",
};

const QUALITY_BY_FORMAT: Record<DerivativeFormat, number> = {
  avif: 60, // AVIF q60 ≈ visually transparent
  webp: 0.82,
  jpg: 0.85,
};

export interface DerivativeRecord {
  format: DerivativeFormat;
  width: number;
  height: number;
  byteSize: number;
  storagePath: string;
  publicUrl: string;
}

export interface ProcessedCarImage {
  /** UUID grouping the 9 derivatives that came from one source upload. */
  imageId: string;
  /** Canonical URL appended to `cars.images` (1280-WebP). */
  canonicalUrl: string;
  derivatives: DerivativeRecord[];
  originalBytes: number;
  totalDerivativeBytes: number;
}

interface PipelineOptions {
  carId: string;
  userId: string;
  /** Whether this is the very first image for the car (sets is_primary). */
  markFirstAsPrimary?: boolean;
  onDerivativeProgress?: (info: { format: DerivativeFormat; width: number }) => void;
}

// ----- Canvas helpers -----------------------------------------------------

async function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => resolve(img);
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function resizeToCanvas(img: HTMLImageElement, targetWidth: number): HTMLCanvasElement {
  const sourceW = img.naturalWidth;
  const sourceH = img.naturalHeight;
  const w = Math.min(targetWidth, sourceW);
  const h = Math.round((w / sourceW) * sourceH);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  // Better resampling for downscaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(`canvas.toBlob(${mime}) returned null`))),
      mime,
      quality,
    );
  });
}

// ----- AVIF encoder (lazy WASM) -------------------------------------------

let avifEncoderPromise: Promise<typeof import("@jsquash/avif")> | null = null;

async function getAvifEncoder() {
  if (!avifEncoderPromise) {
    avifEncoderPromise = import("@jsquash/avif");
  }
  return avifEncoderPromise;
}

async function encodeAvif(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { encode } = await getAvifEncoder();
  const buffer = await encode(imageData, { quality, speed: 6 });
  return new Blob([buffer], { type: "image/avif" });
}

// ----- Single-derivative builder ------------------------------------------

async function buildDerivative(
  img: HTMLImageElement,
  width: number,
  format: DerivativeFormat,
): Promise<{ blob: Blob; width: number; height: number }> {
  const canvas = resizeToCanvas(img, width);
  const quality = QUALITY_BY_FORMAT[format];
  const blob =
    format === "avif"
      ? await encodeAvif(canvas, quality)
      : await canvasToBlob(canvas, MIME_BY_FORMAT[format], quality);
  return { blob, width: canvas.width, height: canvas.height };
}

// ----- Main entry point ---------------------------------------------------

export async function uploadCarImage(file: File, opts: PipelineOptions): Promise<ProcessedCarImage> {
  const validation = validateImageFile(file);
  if (validation) throw new Error(validation.message);

  const img = await loadImage(file);
  try {
    const imageId = crypto.randomUUID();
    const derivatives: DerivativeRecord[] = [];
    let totalDerivativeBytes = 0;

    for (const width of DERIVATIVE_WIDTHS) {
      for (const format of DERIVATIVE_FORMATS) {
        opts.onDerivativeProgress?.({ format, width });
        const { blob, width: actualW, height: actualH } = await buildDerivative(img, width, format);
        const storagePath = `${opts.userId}/${imageId}/${width}.${format}`;
        const { error: uploadErr } = await supabase.storage
          .from("car-images")
          .upload(storagePath, blob, {
            // Path is content-addressed (UUID + width + format), so we can
            // safely cache for a year and treat it as immutable.
            cacheControl: "31536000",
            upsert: false,
            contentType: MIME_BY_FORMAT[format],
          });
        if (uploadErr) throw new Error(`Upload failed (${format} ${width}w): ${uploadErr.message}`);

        const { data: urlData } = supabase.storage.from("car-images").getPublicUrl(storagePath);
        derivatives.push({
          format,
          width: actualW,
          height: actualH,
          byteSize: blob.size,
          storagePath,
          publicUrl: urlData.publicUrl,
        });
        totalDerivativeBytes += blob.size;
      }
    }

    // Pick canonical URL: 1280-WebP (best balance of quality & compatibility).
    const canonical =
      derivatives.find((d) => d.format === "webp" && d.width === 1280) ??
      derivatives.find((d) => d.format === "webp") ??
      derivatives[0];

    // Persist manifest rows. A single batch insert keeps RLS roundtrips low.
    const { error: insertErr } = await supabase.from("car_image_manifest").insert(
      derivatives.map((d) => ({
        car_id: opts.carId,
        image_id: imageId,
        format: d.format,
        width: d.width,
        height: d.height,
        byte_size: d.byteSize,
        storage_path: d.storagePath,
        public_url: d.publicUrl,
        is_primary: opts.markFirstAsPrimary === true && d === canonical,
      })),
    );
    if (insertErr) {
      // Manifest insert failed but files are uploaded — surface the error so
      // the admin can retry, but don't try to delete the storage objects
      // (we don't want partial state from a network blip to nuke the upload).
      throw new Error(`Manifest insert failed: ${insertErr.message}`);
    }

    return {
      imageId,
      canonicalUrl: canonical.publicUrl,
      derivatives,
      originalBytes: file.size,
      totalDerivativeBytes,
    };
  } finally {
    URL.revokeObjectURL(img.src);
  }
}

/**
 * Format a byte count for compact display in admin UI.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
